import { PortfolioTotalValueCalculateType } from '../modules/settings';
import { SymbolConstants } from '../constants/symbols.constants';
import { Side } from '../modules/order/oms/models/orderModels.model';
import { OwnerTypeEnum, ISymbol } from '../modules/shared/models/symbol.model';
import { RED } from '../constants/symbol-group-type.constant';

export const calculateQuantityByAccountBalanceAndPrice = (
	side: Side,
	accountBalance: number,
	quantity: number,
	price: number,
	totalBuyOrSellCommission: number,
	asset: number,
	hidePrice = 0
): number => {
	if (!price || isNaN(price) || price === 0) return quantity;
	let calc = 0;
	if (side === Side.Buy) calc = Math.floor(accountBalance / (price * (1 + totalBuyOrSellCommission) + hidePrice));
	if (side === Side.Sell) return asset < quantity ? asset : quantity;
	return quantity < calc ? quantity : calc;
};
export const calculateQuantityFromDesiredExpenseAndPrice = (
	side: Side,
	desiredExpense: number,
	price: number,
	totalBuyOrSellCommission: number,
	hidePrice = 0,
	cSize = 1
): number => {
	if (!price || isNaN(price) || price === 0) return 0;
	switch (side) {
		case Side.Buy:
			return Math.floor(desiredExpense / (price * (1 + totalBuyOrSellCommission) + hidePrice) / cSize);
		case Side.Sell:
			return Math.ceil(desiredExpense / (price * (1 - totalBuyOrSellCommission)) / cSize);
		default:
			return 0;
	}
};
//محاسبه مبلغ کل
export const calculateDesiredExpenseFromQuantityAndPrice = (
	side: Side,
	quantity: number,
	price: number,
	totalBuyOrSellCommission: number,
	hidePrice = 0,
	cSize = 1
): number => {
	if (!price || isNaN(price) || price === 0) return 0;
	switch (side) {
		case Side.Buy:
			return Math.ceil(quantity * (price * (1 + totalBuyOrSellCommission) + hidePrice)) * cSize;
		case Side.Sell:
			return Math.floor(quantity * (price * (1 - totalBuyOrSellCommission))) * cSize;
		default:
			return 0;
	}
};
// قیمت سر به سر
export const calculateBreakEvenPrice = (
	price: number,
	sellCommission: number,
	buyComission: number,
	hidePrice = 0
): number => Math.round(((1 + buyComission) / (1 - sellCommission)) * price) + hidePrice;
// هزینه معامله
export const calculateDealExpense = (totalBuyOrSellCommission: number, price: number, quantity: number): number =>
	Math.round(totalBuyOrSellCommission * price * quantity);

//هزینه پدیره نویسی پنهان
export const calculateHidePrice = (quantity: number, hidePrice = 0): number => Math.round(quantity * hidePrice);

// جمع کل
export const calculateTotalExpense = (
	side: Side,
	price: number,
	quantity: number,
	totalCommission: number,
	hidePrice = 0
): number => {
	switch (side) {
		case Side.Buy:
			return Math.round((1 + totalCommission) * price * quantity) + quantity * hidePrice;
		case Side.Sell:
			return Math.round((1 - totalCommission) * price * quantity);
		default:
			return 0;
	}
};

// حداقل خرید
export const calculateMinimumBuyQuantity = (symbol: Partial<ISymbol>, price: number): number => {
	let minimumBuyTotalExpense = 5_000_000;
	if (SymbolConstants.BazarPayehMarketUnits.includes(symbol.marketUnit)) minimumBuyTotalExpense = 50_000_000;
	if (SymbolConstants.EtfMarketUnits.includes(symbol.marketUnit)) minimumBuyTotalExpense = 1_000_000;
	return Math.ceil(minimumBuyTotalExpense / (price + symbol.hidePrice));
};

// حداقل فروش
export const calculateMinimumSellQuantity = (
	symbol: ISymbol,
	price: number,
	ownQuantity: number,
	comission: number
): number => {
	const minimumSellIFB = 50_000;
	const minimumSellTSE = 5_000_000;
	let calcedQuantity = 0;
	if (symbol.owner === OwnerTypeEnum.IFB) {
		// فرابورسی
		calcedQuantity = Math.ceil(minimumSellIFB / (price * (1 - comission)));
	} else if (symbol.owner === OwnerTypeEnum.TSE) {
		// بورسی
		calcedQuantity = Math.ceil(minimumSellTSE / (price * (1 - comission)));
	}

	return ownQuantity < calcedQuantity ? ownQuantity : calcedQuantity;
};

const IcebergLowMinValidQuantity = 1000;
const IcebergLowMaxValidQuantity = 30000;
const IcebergHighMaxValidQuantity = 100000;

// آستانه پایین حجم نمایشی
export const calculateMinIceberg = (quantity: number): number => {
	if (!quantity || isNaN(quantity) || quantity < IcebergLowMinValidQuantity) return 0;
	const low = Math.ceil(quantity * 0.3);
	if (low > IcebergLowMaxValidQuantity) return IcebergLowMaxValidQuantity;
	if (low >= IcebergLowMinValidQuantity) {
		return low;
	}
	return IcebergLowMinValidQuantity;
};

// آستانه بالای حجم نمایشی
export const calculateMaxIceberg = (quantity: number): number => {
	if (!quantity || isNaN(quantity) || quantity < IcebergLowMinValidQuantity) return 0;
	if (quantity > IcebergHighMaxValidQuantity) return IcebergHighMaxValidQuantity;
	return quantity;
};

export function calculateClosingPriceVarPercent(
	closePrice?: number,
	feeOfPreviousDaysClosingPrice?: number,
	totalNumberOfSharesTraded?: number
): number {
	if (
		feeOfPreviousDaysClosingPrice === 0 ||
		!feeOfPreviousDaysClosingPrice ||
		!totalNumberOfSharesTraded ||
		totalNumberOfSharesTraded === 0
	)
		return 0;

	if (closePrice > 0 && feeOfPreviousDaysClosingPrice > 0)
		return Math.round(((closePrice - feeOfPreviousDaysClosingPrice) / feeOfPreviousDaysClosingPrice) * 10000) / 100;
	return 0;
}

export function getCurrentValue(
	closingPrice: number,
	lastTradedPrice: number,
	sellCommission: number,
	calculateType: PortfolioTotalValueCalculateType,
	stateColor: string,
	quantity: number,
	isMarketOpen: boolean,
	cSize = 1
) {
	let mainFactor = 0;
	if (!(closingPrice || lastTradedPrice || sellCommission)) return 0;
	else if (stateColor === RED) {
		mainFactor = closingPrice;
	} else if (calculateType === PortfolioTotalValueCalculateType.ClosePrice) {
		mainFactor = closingPrice;
	} else if (calculateType === PortfolioTotalValueCalculateType.LastTradedPrice) {
		mainFactor = lastTradedPrice;
	} else if (calculateType === PortfolioTotalValueCalculateType.Automatic) {
		mainFactor = isMarketOpen ? lastTradedPrice : closingPrice;
	}
	return quantity * mainFactor * cSize * (1 - (sellCommission ?? 0));
}
